import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import journeyTracker from '../utils/journeyTracker';
import { FiChevronUp, FiChevronDown, FiSettings, FiInfo } from "react-icons/fi";
import CompanyRow, { CompanyRowTooltips } from "./CompanyRow";
import EconomicEventRow from "./EconomicEventRow";
import EconomicEventControls from "./EconomicEventControls";
import Tooltip from './Tooltip';
import SortableHeader from './SortableHeader';
import ColumnSettingsModal from './ColumnSettingsModal';
import Modal from 'react-modal';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { estimateNextEarningsDate } from "./utils";
import { ALL_COLUMNS } from '../config/columnConfig';
import NotificationBell from './NotificationBell';
import FavoriteButton from './FavoriteButton';
import AddAllToWatchlistButton from './AddAllToWatchlistButton';
import ListShareButton from './ListShareButton';
import { useListPerformance, useScopedListPerformance, useCallHighlights, useCallGuidance, useMoverNews } from '../services/api';
import { explanationFor } from '../utils/moverNews';
import { isStrategyTab } from '../utils/strategyTabs';

// A sort key is either a column's `sortField` or, when it has none, its `key`.
// Both the header handler and the comparator have to resolve it the same way.
const findColumnForSortKey = (columns, key) =>
  columns.find((col) => col.sortField === key || col.key === key);

// Shared green/red/grey scheme for both the daily badge and the period
// pills below, so the two never drift into two copies of the same rule.
const PERFORMANCE_COLORS = {
  positive: { backgroundColor: '#dcfce7', color: '#15803d' },
  negative: { backgroundColor: '#fee2e2', color: '#dc2626' },
  neutral: { backgroundColor: '#f3f4f6', color: '#6b7280' },
};

function performanceColorFor(avgChange) {
  if (avgChange > 0) return PERFORMANCE_COLORS.positive;
  if (avgChange < 0) return PERFORMANCE_COLORS.negative;
  return PERFORMANCE_COLORS.neutral;
}

// Display order + label for the `periods` keys screen_query's
// PERFORMANCE_PERIODS produces (1W/1M/3M/1Y) -- defined once here so the
// mapping to W/M/Q/Y isn't hand-copied anywhere else.
const PERIOD_PILL_LABELS = [
  { key: '1W', label: 'W' },
  { key: '1M', label: 'M' },
  { key: '3M', label: 'Q' },
  { key: '1Y', label: 'Y' },
];

// Performance summary badge for list description
const ListPerformanceSummary = ({ listId, performanceData }) => {
  const perf = performanceData?.[listId];
  if (!perf || (perf.gainers === 0 && perf.losers === 0)) return null;

  const changeText = perf.avgChange != null
    ? `${perf.avgChange > 0 ? '+' : ''}${perf.avgChange.toFixed(1)}%`
    : null;

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 10px',
    marginLeft: '12px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '12px',
    whiteSpace: 'nowrap',
    ...performanceColorFor(perf.avgChange),
  };

  return (
    <>
      <span
        style={badgeStyle}
        title={`${perf.gainers} up, ${perf.losers} down of ${perf.stocksWithData} stocks`}
      >
        {changeText && <span>{changeText}</span>}
        <span style={{ opacity: 0.7 }}>
          {perf.gainers}↑ / {perf.losers}↓
        </span>
      </span>
      {perf.periods && (
        <span style={{ display: 'inline-flex', gap: '4px', marginLeft: '6px', whiteSpace: 'nowrap', flexWrap: 'wrap' }}>
          {PERIOD_PILL_LABELS.map(({ key, label }) => {
            const p = perf.periods[key];
            if (!p || p.avgChange == null) return null;

            const pillStyle = {
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '2px 6px',
              fontSize: '11px',
              fontWeight: 600,
              borderRadius: '10px',
              whiteSpace: 'nowrap',
              ...performanceColorFor(p.avgChange),
            };
            const pctText = `${p.avgChange > 0 ? '+' : ''}${p.avgChange.toFixed(1)}%`;

            return (
              <span
                key={key}
                style={pillStyle}
                title={`${key}: ${p.gainers} up, ${p.losers} down of ${p.stocksWithData} stocks (vs close on ${p.asOf})`}
              >
                <span style={{ opacity: 0.7, fontWeight: 500 }}>{label}</span>
                <span>{pctText}</span>
              </span>
            );
          })}
        </span>
      )}
    </>
  );
};

// A row reference can be a ticker string or the row object itself.
const getSymbol = (r) => (typeof r === 'string' ? r : (r?.tickerSymbol || r?.symbol || r?.ticker));

// Define the sanitizeId function
function sanitizeId(id) {
  const sanitized = id.replace(/[^a-zA-Z0-9-_]/g, '');
  console.log(`Original ID: ${id}, Sanitized ID: ${sanitized}`);
  return sanitized;
}

Modal.setAppElement('#root'); // Add this line if not already set

// Tabs whose symbol set exists only in the browser (no server-side list): the strategy,
// label and ad-hoc symbol tabs. Their performance badge is computed from the loaded rows.
const isBrowserSymbolSetTab = (tab) =>
  tab === 'url_symbols' || tab?.startsWith('label_') || tab?.startsWith('symbols_') || isStrategyTab(tab);

const formatJsonContent = (text) => {
  try {
    // Handle label objects (from label tabs)
    if (text && typeof text === 'object' && text.category === 'My Lists') {
      // inline-flex, not flex: a block here pushes the performance badge and the action
      // pills that follow it onto their own line (`.inline-flex` is absent from the prebuilt
      // Tailwind output, hence the inline style).
      return (
        <span className="label-explanation" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span className="text-gray-500 text-sm">{text.category}</span>
          <span className="text-gray-400">›</span>
          <span className="font-medium text-gray-800">{text.name}</span>
          {text.description && (
            <span className="text-gray-400 text-sm">({text.description})</span>
          )}
        </span>
      );
    }

    // Check if this is a custom filter explanation (contains "Filter Logic" or "Conditions:")
    if (typeof text === 'string' && (text.includes('Filter Logic:') || text.includes('Conditions:'))) {
      // Format the custom filter explanation with better styling
      return (
        <div className="custom-filter-explanation">
          {text.split('\n').map((line, index) => {
            if (line.startsWith('Filter Logic:')) {
              return <div key={index} className="font-semibold text-blue-700">{line}</div>;
            } else if (line.startsWith('•')) {
              return <div key={index} className="ml-4 text-gray-700">{line}</div>;
            } else if (line.trim() === 'Conditions:') {
              return <div key={index} className="font-medium mt-1 mb-1">{line}</div>;
            } else if (line.trim() === '') {
              return <div key={index} className="h-2"></div>;
            } else {
              return <div key={index}>{line}</div>;
            }
          })}
        </div>
      );
    }
    
    // Handle "Custom filter:" format
    if (typeof text === 'string' && text.startsWith('Custom filter:')) {
      const filterName = text.replace('Custom filter:', '').trim();
      return (
        <div className="custom-filter-title flex items-center">
          <span className="custom-filter-label">Custom filter:</span>
          <span className="custom-filter-name">{filterName}</span>
        </div>
      );
    }
    
    // Original JSON formatting logic for other types of content
    // Split the text into description and sections
    const [description, sectorAnalysis, riskMetrics] = text.split(/Sector Analysis:|Risk Metrics:/);
    
    if (!sectorAnalysis || !riskMetrics) return <span>{text}</span>;

    // Parse the JSON parts
    const sectorData = JSON.parse(sectorAnalysis.replace(/'/g, '"').trim());
    const riskData = JSON.parse(riskMetrics.replace(/'/g, '"').trim());

    return (
      <div className="explanation-content">
        <div className="description">{description.trim()}</div>
        <div className="metric-row">
          <span>Current:</span>
          {Object.entries(sectorData.current).map(([sector, value]) => (
            <span key={sector} className={sector.toLowerCase()}>
              {sector} {value}%
            </span>
          ))}
          <span className="spacer">|</span>
          <span>Suggested:</span>
          {Object.entries(sectorData.suggested).map(([sector, value]) => (
            <span key={sector} className={sector.toLowerCase()}>
              {sector} {value}%
            </span>
          ))}
        </div>
        <div className="metric-row">
          <span>Diversification Score: </span>
          <span className="score">{riskData.diversification_score}</span>
          <span className="risk-tag">Low</span>
          <span className="spacer">|</span>
          <span>Sector Concentration: </span>
          <span className="score">{riskData.sector_concentration}</span>
          <span className="risk-tag">High Risk</span>
          <span className="spacer">|</span>
          <span className="risks">
            {riskData.key_risks.map((risk, i) => 
              `${i > 0 ? ' • ' : '• '}${risk}`
            )}
          </span>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error formatting content:', error);
    return <span>{text}</span>;
  }
};

const StockTable = React.memo(({
  companyData = [],
  economicEvents = [],
  econAnalysis = null,
  onAddToWatchList,
  onDelete,
  loadingTable,
  selectedTimeframes,
  setSelectedTimeframes,
  geography,
  setGeography,
  showEconomicEvents,
  setShowEconomicEvents,
  selectedTab,
  colorSettings,
  columns,
  onColumnReorder,
  onColorSettingsClick,
  userSettings,
  onSettingsChange,
  news = [],
  listExplanation = '',
  onNoteChange,
  onNotInterested,
  loggedInUser,
  existingWatchlistSymbols = [],
  onBatchAddComplete,
  // Labels props
  labelsData = { labels: [], assignments: {} },
  onOpenLabelManager,
  onOpenPurchaseInfo,
  // Extension integration
  initialExpandSymbol = null,
  // { symbol, nonce } — expand + scroll to that row. The nonce lets the same
  // symbol be re-focused on a repeat click (see App.js handleFocusSymbol).
  focusSymbol = null,
  // The list's shareable /list/<slug> path (smart lists only, else null) — App.js resolves it.
  listPath = null,
}) => {
  // Ensure the actions column is only added if not already present
  const actionColumn = ALL_COLUMNS.find((col) => col.key === 'actions');
  const notesColumn = ALL_COLUMNS.find((col) => col.key === 'notes');
  
  // Start with the user's configured columns
  let displayColumns = [...columns];
  
  // Add the actions column if not already present
  if (!displayColumns.some((col) => col.key === 'actions')) {
    displayColumns.push(actionColumn);
  }
  
  // For watchlist tab, conditionally add the notes column if not already present
  if (selectedTab === 'watchlist' && !displayColumns.some((col) => col.key === 'notes')) {
    // Add notes column to the end
    displayColumns.push(notesColumn);
  }
  
  // Make sure to remove notes column for non-watchlist tabs
  if (selectedTab !== 'watchlist') {
    displayColumns = displayColumns.filter(col => col.key !== 'notes');
  }
  
  const displayedColumns = displayColumns;

  // Fetch list performance data for the summary badge. The public map covers every smart
  // list; the watchlist (issue #335) and the browser-only symbol tabs (issue #337) get a
  // per-user entry from their own authenticated call, fetched only while that tab shows.
  // For a browser-only tab the loaded rows ARE the list, so their tickers are what is sent.
  const { data: listPerformance = {} } = useListPerformance();
  const scopedSymbols = useMemo(() => {
    if (selectedTab === 'watchlist') return existingWatchlistSymbols;
    if (isBrowserSymbolSetTab(selectedTab)) return companyData.map((c) => c.tickerSymbol).filter(Boolean);
    return [];
  }, [selectedTab, existingWatchlistSymbols, companyData]);
  const { data: scopedPerformance = {} } = useScopedListPerformance(selectedTab, loggedInUser?.email, scopedSymbols);
  const performanceData = useMemo(
    () => ({ ...listPerformance, ...scopedPerformance }),
    [listPerformance, scopedPerformance]
  );

  // Add debug logging
  console.log('StockTable economicEvents:', economicEvents);

  // Initialize sortConfig based on the first column
  const [sortConfig, setSortConfig] = useState({
    key: columns[0]?.sortField || columns[0]?.key || 'date',
    direction: 'ascending',
  });
  const [expandedRow, setExpandedRow] = useState(initialExpandSymbol);
  const prevExpandedRef = useRef(null);
  const hasAutoExpandedRef = useRef(false);
  const scrollRef = useRef(null);
  const handledFocusRef = useRef(0);

  // Auto-expand row when initialExpandSymbol is provided and data loads
  useEffect(() => {
    console.log('[EXT-EXPAND] useEffect triggered:', {
      initialExpandSymbol,
      dataLength: companyData.length,
      hasAutoExpanded: hasAutoExpandedRef.current
    });
    if (initialExpandSymbol && companyData.length > 0 && !hasAutoExpandedRef.current) {
      // Check if the symbol exists in the data
      const matchingRow = companyData.find(
        c => c.tickerSymbol?.toUpperCase() === initialExpandSymbol.toUpperCase()
      );
      console.log('[EXT-EXPAND] Looking for symbol:', initialExpandSymbol, 'Found:', matchingRow?.tickerSymbol);
      if (matchingRow) {
        console.log('[EXT-EXPAND] Auto-expanding row:', matchingRow.tickerSymbol);
        setExpandedRow(matchingRow.tickerSymbol);
        hasAutoExpandedRef.current = true;
      }
    }
  }, [initialExpandSymbol, companyData]);

  const toggleRow = useCallback((row) => {
    setExpandedRow(prevRow => {
      const newExpanded = prevRow === row ? null : row;

      if (prevExpandedRef.current && prevExpandedRef.current !== row) {
        // Previous row was collapsed - stop tracking its dwell time
        journeyTracker.stopStockExpand(getSymbol(prevExpandedRef.current));
      }

      if (newExpanded) {
        // New row expanded - start tracking dwell time
        journeyTracker.startStockExpand(getSymbol(newExpanded));
      } else if (prevRow) {
        // Row collapsed - stop tracking
        journeyTracker.stopStockExpand(getSymbol(prevRow));
      }

      prevExpandedRef.current = newExpanded;
      return newExpanded;
    });
  }, []);

  // Focus request from elsewhere on the page (Signal Watches widget): expand the
  // matching row and scroll it into view. Runs again on companyData changes so a
  // request made while another list was loaded resolves once the data lands;
  // handledFocusRef stops it from re-expanding on later background refetches.
  useEffect(() => {
    const requested = focusSymbol?.symbol;
    if (!requested || handledFocusRef.current === focusSymbol.nonce) return;

    const match = companyData.find(
      (c) => c.tickerSymbol?.toUpperCase() === requested.toUpperCase()
    );
    if (!match) return; // not in this list (yet) — wait for the next data load

    handledFocusRef.current = focusSymbol.nonce;
    setExpandedRow((prev) => {
      if (prev !== match.tickerSymbol) {
        if (prevExpandedRef.current) journeyTracker.stopStockExpand(getSymbol(prevExpandedRef.current));
        journeyTracker.startStockExpand(match.tickerSymbol);
        prevExpandedRef.current = match.tickerSymbol;
      }
      return match.tickerSymbol;
    });

    // Rows aren't virtualized, so the node exists as soon as the data is in.
    requestAnimationFrame(() => {
      const row = scrollRef.current?.querySelector(`tr[data-ticker="${match.tickerSymbol}"]`);
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [focusSymbol, companyData]);

  const requestSort = useCallback((key) => {
    const column = findColumnForSortKey(displayedColumns, key);
    if (column && column.isSortable) {
      setSortConfig((prevConfig) => {
        const direction =
          prevConfig.key === key && prevConfig.direction === 'ascending'
            ? 'descending'
            : 'ascending';
        // Track sort action
        journeyTracker.trackFilterChange('sort', { column: key, direction });
        return { key, direction };
      });
    }
  }, [displayedColumns]);

  // The Guidance column (issue #306) is opt-in and most rows have no analysed call, so its data is
  // fetched only while the column is displayed — then for every row at once, in one slim request.
  // It is merged onto the rows below so the column's sortFunction and the cell read one field.
  const showGuidance = displayedColumns.some((col) => col?.key === 'guidance');
  const { data: guidanceMap } = useCallGuidance(
    showGuidance ? companyData.map((company) => company.tickerSymbol) : [],
    loggedInUser?.email,
  );

  const combinedData = useMemo(() => {
    // console.log('Processing company data:', companyData);

    const companyRows = companyData.map((company) => ({
      ...company,
      date: company.earningsDate || estimateNextEarningsDate(company.lastEarningsDate) || null,
      guidance: guidanceMap?.[company.tickerSymbol] ?? null,
      type: "company",
    }));

    const economicRows = economicEvents.map((event) => ({
      ...event,
      type: "economic",
    }));

    // Don't pre-sort here; let sortFunction handle it
    return [...companyRows, ...economicRows];
  }, [companyData, economicEvents, guidanceMap]);

  const sortedData = useMemo(() => {
    console.log('Sorting data:', combinedData);
    // Both of these are fixed for the whole sort, so resolve them once here
    // rather than re-scanning every column on each of the ~n log n comparisons.
    const key = sortConfig.key;
    const column = findColumnForSortKey(displayedColumns, key);

    return [...combinedData].sort((a, b) => {
      // Use column's custom sortFunction if available
      if (column?.sortFunction) {
        return sortConfig.direction === 'ascending' 
          ? column.sortFunction(a, b)
          : column.sortFunction(b, a);
      }

      // Fallback to default sorting logic
      let aValue = a[key];
      let bValue = b[key];

      // Handle undefined or null values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      const aValueType = typeof aValue;
      const bValueType = typeof bValue;

      if (aValueType === 'string' && bValueType === 'string') {
        return sortConfig.direction === 'ascending'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (aValueType === 'number' && bValueType === 'number') {
        return sortConfig.direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue;
      } else {
        // Convert both values to strings for comparison
        const aString = aValue.toString();
        const bString = bValue.toString();
        return sortConfig.direction === 'ascending'
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      }
    });
  }, [combinedData, sortConfig, displayedColumns]);

  const dateColumnStyle = useMemo(() => ({
    width: "90px",
    minWidth: "90px",
    maxWidth: "90px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }), []);

  // Fetched only for the row the user actually opened. The table can hold 200 rows and almost
  // none of them have a recent call, so fetching the whole page would be mostly empty responses.
  const { data: callHighlightsMap } = useCallHighlights(
    expandedRow ? [expandedRow] : [],
    loggedInUser?.email,
  );

  // Why it moved (issue #317): ONE request for the whole page, unlike the call highlights above —
  // the response carries only the tickers that moved 5%+ (a handful), so it stays small, and a
  // theme member needs the other members' names, which a single-ticker request would not return.
  const { data: moverNewsData } = useMoverNews(companyData.map((company) => company.tickerSymbol));

  const renderCompanyRow = useCallback(
    (item, index) => (
      <CompanyRow
        key={item.tickerSymbol || `company-${index}`}
        company={item}
        colorSettings={colorSettings}
        expandedRow={expandedRow}
        toggleRow={toggleRow}
        onAddToWatchList={onAddToWatchList}
        onDelete={onDelete}
        onNotInterested={onNotInterested}
        dateColumnStyle={dateColumnStyle}
        selectedTab={selectedTab}
        listName={listExplanation?.name || selectedTab}
        columns={displayedColumns}
        news={news}
        onNoteChange={onNoteChange}
        labelsData={labelsData}
        userEmail={loggedInUser?.email}
        onOpenLabelManager={onOpenLabelManager}
        onOpenPurchaseInfo={onOpenPurchaseInfo}
        callHighlights={callHighlightsMap?.[item.tickerSymbol]}
        moverNews={explanationFor(moverNewsData, item.tickerSymbol)}
      />
    ),
    [expandedRow, toggleRow, onAddToWatchList, onDelete, onNotInterested, dateColumnStyle,
     selectedTab, listExplanation, colorSettings, displayedColumns, news, onNoteChange,
     callHighlightsMap, moverNewsData, labelsData, loggedInUser, onOpenLabelManager, onOpenPurchaseInfo]
  );

  const renderEconomicEventRow = useCallback((event, index) => (
    <EconomicEventRow
      key={`economic-${index}`}
      event={event}
      expandedRow={expandedRow}
      toggleRow={toggleRow}
      index={index}
      dateColumnStyle={dateColumnStyle}
      totalColumns={displayedColumns.length} // Pass the correct totalColumns
    />
  ), [expandedRow, toggleRow, dateColumnStyle, displayedColumns]);

  const renderTableBody = () => {
    if (loadingTable) {
      return (
        <tr>
          <td colSpan={displayedColumns.length} className="text-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mx-auto"></div>
            <p>Loading...</p>
          </td>
        </tr>
      );
    }

    console.log('Rendering table body with data:', sortedData);
    return sortedData.map((item, index) => {
     // console.log('Rendering item:', item);
      switch (item.type) {
        case "company":
          return renderCompanyRow(item, index);
        case "economic":
          return renderEconomicEventRow(item, index);
        default:
          console.log('Unknown item type:', item);
          return null;
      }
    });
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = displayedColumns.findIndex((col) => col.key === active.id);
      const overIndex = displayedColumns.findIndex((col) => col.key === over.id);

      const activeColumn = displayedColumns[activeIndex];
      const overColumn = displayedColumns[overIndex];

      if (activeColumn.isActionsColumn || overColumn.isActionsColumn) {
        // Do not allow moving the Actions column
        return;
      }

      const newColumns = [...displayedColumns];

      // Remove the item being dragged
      const [movedColumn] = newColumns.splice(activeIndex, 1);

      // Insert the item at the new position
      newColumns.splice(overIndex, 0, movedColumn);

      // Ensure Actions column stays after the Company column
      const companyIndex = newColumns.findIndex((col) => col.isCompanyColumn);
      const actionsIndex = newColumns.findIndex((col) => col.isActionsColumn);

      // Remove Actions column
      const [actionsColumn] = newColumns.splice(actionsIndex, 1);

      // Reinsert Actions column after the Company column
      newColumns.splice(companyIndex + 1, 0, actionsColumn);

      onColumnReorder(newColumns);
    }
  }, [displayedColumns, onColumnReorder]);

  // State to control ColumnSettingsModal visibility
  const [isColumnSettingsModalOpen, setIsColumnSettingsModalOpen] = useState(false);

  // Track if user has discovered column settings (persisted in localStorage)
  const [hasDiscoveredColumnSettings, setHasDiscoveredColumnSettings] = useState(() => {
    return localStorage.getItem('hasDiscoveredColumnSettings') === 'true';
  });

  const openColumnSettingsModal = useCallback(() => {
    console.log('Settings icon clicked');
    setIsColumnSettingsModalOpen(true);
    // Mark as discovered
    if (!hasDiscoveredColumnSettings) {
      localStorage.setItem('hasDiscoveredColumnSettings', 'true');
      setHasDiscoveredColumnSettings(true);
    }
  }, [hasDiscoveredColumnSettings]);

  const closeColumnSettingsModal = useCallback(() => {
    setIsColumnSettingsModalOpen(false);
  }, []);

  const handleSaveColumns = useCallback((selectedColumnKeys) => {
    // Filter out the notes column from user selection if it's there,
    // since we handle it separately by adding it dynamically
    const filteredColumnKeys = selectedColumnKeys.filter(key => key !== 'notes');
    
    // Filter the selected columns from ALL_COLUMNS to maintain consistent order
    const selectedColumns = ALL_COLUMNS.filter((col) =>
      filteredColumnKeys.includes(col.key)
    );

    // Find indexes of Company and Actions columns
    const companyIndex = selectedColumns.findIndex((col) => col.key === 'company');
    const actionsIndex = selectedColumns.findIndex((col) => col.key === 'actions');

    // Ensure Actions column is immediately after Company
    if (actionsIndex !== -1 && companyIndex !== -1 && actionsIndex !== companyIndex + 1) {
      // Remove Actions column from its current position
      const [actionsColumn] = selectedColumns.splice(actionsIndex, 1);
      // Insert Actions column immediately after Company column
      selectedColumns.splice(companyIndex + 1, 0, actionsColumn);
    }

    // Update columns
    onColumnReorder(selectedColumns);
  }, [onColumnReorder]);

  // Add console log to verify settings updates
  const handleSettingsChange = useCallback((category, value) => {
    console.log('StockTable handleSettingsChange:', { category, value });
    if (onSettingsChange) {
      onSettingsChange(category, value);
    }
  }, [onSettingsChange]);

  // Add debug log
  console.log('StockTable rendering with economicEvents:', economicEvents);

  // maxHeight makes the wrapper the vertical scroller — position:sticky on the
  // header cells can't stick to page scroll from inside an overflow-auto ancestor
  return (
    <div ref={scrollRef} className="overflow-auto" style={{ maxHeight: 'calc(100vh - 16px)' }}>
      {/* Wrap ColumnSettingsModal with Modal component */}
      <Modal
        isOpen={isColumnSettingsModalOpen}
        onRequestClose={closeColumnSettingsModal}
        contentLabel="Column Settings"
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <ColumnSettingsModal
          columns={columns}
          onSave={handleSaveColumns}
          onClose={closeColumnSettingsModal}
        />
      </Modal>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table className="min-w-full bg-white">
          <colgroup>
            {displayedColumns.map((column, index) => (
              <col key={`col-${column.key}-${index}`} />
            ))}
          </colgroup>
          <thead>
            <SortableContext
              items={displayedColumns
                .filter((col) => !col.isFixed && !col.isActionsColumn)
                .map((col) => col.key)}
              strategy={horizontalListSortingStrategy}
            >
              <tr className="bg-gray-100">
                {displayedColumns.map((column, index) => (
                  <SortableHeader
                    key={`header-${column.key}-${index}`}
                    column={column}
                    requestSort={requestSort}
                    sortConfig={sortConfig}
                    onSettingsClick={openColumnSettingsModal}
                    onColorSettingsClick={onColorSettingsClick}
                    showSettingsGlow={!hasDiscoveredColumnSettings}
                  />
                ))}
              </tr>
            </SortableContext>
          </thead>
          <tbody>
            <tr>
              <td colSpan={displayedColumns.length} className="p-0">
                <EconomicEventControls
                  userSettings={userSettings}
                  onSettingsChange={handleSettingsChange}
                  showEconomicEvents={showEconomicEvents}
                  setShowEconomicEvents={setShowEconomicEvents}
                  analysis={econAnalysis}
                />
              </td>
            </tr>

            {/* Row for the listExplanation text and the list's action pills. It also renders for a
                shareable list with no description text (listPath set), so its Share button isn't lost. */}
            {(listExplanation || listPath) && (
              <tr className="explanation-row">
                <td colSpan={displayedColumns.length} className="p-3 bg-gray-50 border-b border-gray-200">
                  {listExplanation && <span className="filter-content">{formatJsonContent(listExplanation)}</span>}
                  <ListPerformanceSummary listId={selectedTab} performanceData={performanceData} />
                  {(selectedTab && selectedTab !== 'watchlist') && (
                    <>{listExplanation && <>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</>}<span className="inline-flex items-center gap-2">
                      <AddAllToWatchlistButton
                        symbols={companyData.map(c => c.tickerSymbol).filter(Boolean)}
                        existingWatchlistSymbols={existingWatchlistSymbols}
                        onAddToWatchList={onAddToWatchList}
                        onNoteChange={onNoteChange}
                        onBatchAddComplete={onBatchAddComplete}
                        userEmail={loggedInUser?.email}
                        listName={listExplanation?.name || selectedTab}
                      />
                      <FavoriteButton
                        listId={selectedTab}
                        userEmail={loggedInUser?.email}
                        listName={listExplanation?.name || selectedTab}
                        variant="pill"
                      />
                      <NotificationBell
                        listId={selectedTab}
                        isLoggedIn={!!loggedInUser?.email}
                        userEmail={loggedInUser?.email}
                        variant="pill"
                      />
                      {listPath && <ListShareButton path={listPath} />}
                    </span></>
                  )}
                </td>
              </tr>
            )}

            {renderTableBody()}
          </tbody>
        </table>
      </DndContext>
      <CompanyRowTooltips />
    </div>
  );
});
export default React.memo(StockTable);
